// src/utils/helper.js
import CryptoJS from "crypto-js";
import { useSearchParams } from "react-router-dom";
import { projectType, userAccessType } from "utils/lookUpTable";
import ct from "countries-and-timezones";
import swal from "sweetalert";
import imageCompression from "browser-image-compression";
import parsePhoneNumberFromString from "libphonenumber-js";
import JSZip from "jszip";
import mimeDb from "mime-db";
import moment from "moment";

/**
 * Recursively searches for an object by ID in a nested array.
 * @param {Array} arr - The array to search in.
 * @param {string} id - The ID of the object to find.
 * @returns {Object|null} The found object or null if not found.
 */
export function findObjectById(arr, id) {
  for (const obj of arr) {
    if (obj.id === id) {
      return obj;
    }
    /* if (obj.subData) {
      const result = findObjectById(obj.subData, id);
      if (result) {
        return result;
      }
    } */
  }
  return null;
}

export function findSubObjectById(arr, id, key) {
  for (const obj of arr) {
    if (obj[key]) {
      const result = findObjectById(obj[key], id);
      if (result) {
        return result;
      }
    }
  }
  return null;
}

export function getAccess(id) {
  switch (id.toString()) {
    case "55":
      return 0;
    case "70":
      return 1;
    case "90":
      return 2;
    default:
      return 0;
  }
}

export function getRoleName(accessLevel) {
  switch (accessLevel) {
    case 0:
      return "Super Admin";
    case 1:
      return "Admin";
    case 2:
      return "User";
    default:
      return "";
  }
}

export function isAdmin(accessLevel) {
  switch (accessLevel) {
    case 0:
      return true;
    case 1:
      return true;

    default:
      return false;
  }
}

export function isSuperAdmin(accessLevel) {
  switch (accessLevel) {
    case 0:
      return true;
    case 1:
      return false;

    default:
      return false;
  }
}

export function canWrite(accessLevel) {
  switch (accessLevel) {
    case 0:
      return false;
    case 1:
      return true;
    case 2:
      return true;
    default:
      return false;
  }
}
export function canWriteSuperAdmin(accessLevel) {
  switch (accessLevel) {
    case 0:
      return false;
    case 1:
      return false;
    case 2:
      return true;
    default:
      return false;
  }
}

// Define a secret key for encryption and decryption
const SECRET_KEY = "my_Secret_Key_123";

const { REACT_APP_CRYPTO_KEY, REACT_APP_API_KEY, REACT_APP_TIMOEOUT } =
  process.env;

/*---- Encrypt function  ---*/
export const encryptData = (data) => {
  try {
    return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
  } catch (error) {
    console.error("Encryption error:", error);
    return "";
  }
};

/*---- Decrypt function  ---*/
export const decryptData = (ciphertext) => {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (error) {
    console.error("Decryption error:", error);
    return {};
  }
};

export const decryptCrypto = (val) => {
  try {
    const bytes = CryptoJS.AES.decrypt(val, REACT_APP_CRYPTO_KEY);
    const decryptedPassword = bytes.toString(CryptoJS.enc.Utf8);
    return decryptedPassword;
  } catch (e) {
    return val;
  }
};

export const isNull = (value) => {
  if (value === undefined || value === "undefined") {
    return true;
  }
  if (value === null || value === "null") {
    return true;
  }
  return false;
};

export const isEmpty = (value) => {
  try {
    if (isNull(value)) {
      return true;
    }
    if (value.toString().trim() === "") {
      return true;
    }
    return false;
  } catch (e) {
    console.log(e, "isEmpty in helper/Validation");
    return false;
  }
};

export const isValidFolderId = (value) => {
  if (isEmpty(value)) return false;
  return Number(value) > 0;
};

export const getFolderRedirectUrl = (folderType, projectId, folderId, type) => {
  if (type == 0 && folderType == 1) {
    return `/project-home/generalinformation?projectId=${projectId}&type=0&folderId=${folderId}`;
  } else if (type == 1 && folderType == 1) {
    return `/project-home/occupanthomepage?projectId=${projectId}&type=1&folderId=${folderId}`;
  }
  switch (folderType) {
    case 2:
      return `/document?projectId=${projectId}&folderId=${folderId}&type=${type}`;
    case 3:
      return `/address-book?projectId=${projectId}&folderId=${folderId}&type=${type}`;
    case 4:
      return `/journal?projectId=${projectId}&folderId=${folderId}&type=${type}`;
    case 5:
      return `/cart?projectId=${projectId}&folderId=${folderId}&type=${type}`;
    case 6:
      return `/home?projectId=${projectId}&folderId=${folderId}&type=${type}`;
    case 11:
      return `/photos?projectId=${projectId}&folderId=${folderId}&type=${type}`;
    case 7:
      if (type == 0) {
        return `/project-home/project-information?projectId=${projectId}&folderId=${folderId}&type=${type}`;
      }
      return `/occupant-info?projectId=${projectId}&folderId=${folderId}&type=${type}`;
    default:
      return `/default?projectId=${projectId}&folderId=${folderId}&type=${type}`;
  }
};

export const checkUserPermission = (
  userRoles,
  permissionData,
  userData = {},
) => {
  if (userData && userData.userType == 0) {
    return userAccessType.write;
  }
  const { readRoles, writeRoles } = permissionData;

  const hasWriteRole = userRoles.some((userRole) =>
    writeRoles.some((writeRole) => writeRole.id === userRole.id),
  );
  if (hasWriteRole) {
    return userAccessType.write;
  }
  const hasReadRole = userRoles.some((userRole) =>
    readRoles.some((readRole) => readRole.id === userRole.id),
  );
  if (hasReadRole) {
    return userAccessType.read;
  }
  return userAccessType.none;
};

export const getGmtTimeForTimezone = (timezone) => {
  // Current date in the specified timezone
  const currentDate = new Date().toLocaleString("en-US", {
    timeZone: timezone,
  });

  // Parse the date back to a Date object
  const localDate = new Date(currentDate);

  // Get the UTC time equivalent
  const utcTime = localDate.toUTCString();

  return utcTime;
};

export const formatFileSize = (sizeInKB) => {
  const sizeValue = Number(sizeInKB);
  if (!Number.isFinite(sizeValue) || sizeValue <= 0) return "0 KB";
  if (sizeValue < 1) return `${sizeValue} KB`;
  const units = ["KB", "MB", "GB", "TB"];
  const i = Math.min(
    Math.floor(Math.log(sizeValue) / Math.log(1024)),
    units.length - 1,
  );
  const size = sizeValue / Math.pow(1024, i);
  return `${size.toFixed(2)} ${units[i]}`;
};

export const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, encryptData(data));
  } catch (error) {
    console.error("Error saving data to local storage", error);
    return "";
  }
};

export const readFromLocalStorage = (key) => {
  try {
    const data = localStorage.getItem(key);

    if (data) {
      return decryptData(data);
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error saving data to local storage", error);
    localStorage.clear();
    window.location("/");
  }
};

export const getCountryByID = (countryId) => {
  try {
    if (!countryId) {
      return "";
    }
    const country = ct.getCountry(countryId);
    /*
      {
        id: 'DE',
        name: 'Germany',
        timezones: [ 'Europe/Berlin', 'Europe/Zurich' ]
      }
    */
    return country.name;
  } catch (e) {
    return countryId;
  }
};

export const saveToSessionStorage = (key, data) => {
  try {
    sessionStorage.setItem(key, encryptData(data));
  } catch (error) {
    console.error("Error saving data to local storage", error);
    return "";
  }
};

export const readFromSessionStorage = (key) => {
  try {
    const data = sessionStorage.getItem(key);

    if (data) {
      return decryptData(data);
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error saving data to local storage", error);
    sessionStorage.clear();
    window.location("/");
  }
};

// export const showAlert = (props) => {
//   swal(props);
// };

export const showAlert = (options) => {
  return swal(options); // ✅ fix: return the promise
};

export const getUpdateRequest = (request) => {
  if (request >= 13) {
    return request;
  } else if (request >= 2 && request < 13) {
    return request + 1;
  } else if (request === 1 || request === 2) {
    return 3;
  } else {
    return 3;
  }
};

export const compressImage = async (file) => {
  const options = {
    maxSizeMB: 1, // Maximum size in MB
    maxWidthOrHeight: 500, // Maximum width or height
    useWebWorker: true, // Use web worker for better performance
    initialQuality: 1,
  };

  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    console.error("Error compressing image:", error);
    return file; // Fallback to the original file if compression fails
  }
};

export async function imageHandler() {
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", "image/*");
  input.click();

  input.onchange = async () => {
    const file = input.files[0];
    if (file) {
      const options = {
        maxSizeMB: 1, // Maximum size in MB
        maxWidthOrHeight: 500, // Maximum width or height
        useWebWorker: true, // Use web worker for better performance
        initialQuality: 1, // Set initial quality to 80%
      };

      try {
        const compressedFile = await imageCompression(file, options);
        const reader = new FileReader();
        reader.readAsDataURL(compressedFile);
        reader.onload = () => {
          const imageUrl = reader.result;
          const quill = this.quill;
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", imageUrl, "user");
        };
      } catch (error) {
        console.error("Error compressing image:", error);
      }
    }
  };
}

export const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  const date = new Date(dateString);
  if (isNaN(date)) return "N/A";

  return date
    .toLocaleDateString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
    })
    .replace(/-/g, "-");
};

export const formatPhoneNumber = (cellCode, cell) => {
  if (!cellCode || !cell) return "N/A"; // Handle missing values
  const formattedNumber = cellCode.startsWith("+")
    ? `${cellCode}${cell}`
    : `+${cellCode}${cell}`;
  const parsedNumber = parsePhoneNumberFromString(formattedNumber);

  if (!parsedNumber) return formattedNumber; // Fallback if parsing fails

  return `${parsedNumber.formatInternational()}`;
};

export function formatAlarmDateTime(alarmDate, alarmTime) {
  if (!alarmDate || !alarmTime) {
    return "N/A";
  }

  const date = new Date(`${alarmDate} ${alarmTime}`);

  if (isNaN(date.getTime())) {
    return "N/A";
  }

  const formattedDate = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);

  const formattedTime = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  return `${formattedDate} at ${formattedTime}`;
}

export const getNestedAccess = (obj, path) => {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

export const parseSubmissionNote = (note) => {
  if (!note) return [];

  const lines = note.split(/\r?\n/).map((line) => line.trim());

  const numberedLines = lines.filter((line) => /^\d+\.\s+/.test(line));

  return numberedLines.length > 1 ? numberedLines : null;
};

// const guessMimeType = (fileName) => {
//   const ext = fileName.split(".").pop().toLowerCase();
//   const mimeMap = {
//     txt: "text/plain",
//     json: "application/json",
//     csv: "text/csv",
//     html: "text/html",
//     js: "application/javascript",
//     css: "text/css",
//     xml: "application/xml",
//     jpg: "image/jpeg",
//     jpeg: "image/jpeg",
//     png: "image/png",
//     pdf: "application/pdf",
//     zip: "application/zip",
//   };
//   return mimeMap[ext] || "application/octet-stream";
// };

const getMimeFromExtension = (filename) => {
  const ext = filename.split(".").pop().toLowerCase();
  for (const type in mimeDb) {
    const mime = mimeDb[type];
    if (mime.extensions?.includes(ext)) {
      return type;
    }
  }
  return "application/octet-stream"; // fallback
};

export async function extractZipFiles(zipFile) {
  const zip = await JSZip.loadAsync(zipFile);
  const extractedFiles = [];
  for (const fileName in zip.files) {
    const entry = zip.files[fileName];
    if (!entry.dir) {
      const blob = await entry.async("blob");
      const arrayBuffer = await blob.arrayBuffer();
      const buffer = new Uint8Array(arrayBuffer);
      extractedFiles.push({
        fileName: entry.name,
        fileSize: buffer.length,
        fileType: getMimeFromExtension(entry.name),
        buffer: buffer,
      });
    }
  }
  return extractedFiles;
}

export const getFileType = (file) => {
  return file.type || getMimeFromExtension(file.name);
};

export function convertKB(kb) {
  const mb = kb / 1024;
  const gb = mb / 1024;

  if (gb >= 1) return gb.toFixed(2) + " GB";
  if (mb >= 1) return mb.toFixed(2) + " MB";
  return kb + " KB";
}

export function formatDateTime(dateString, format = "DD-MM-YYYY • hh:mm A") {
  // Example output: "Dec 10, 2025 • 12:00 AM"
  return moment(dateString).format(format);
}

export const AIServices = () => {
  console.log(process.env.REACT_APP_AI_SERVICE, "servicesss");
  if (process.env.REACT_APP_AI_SERVICE) {
    return true;
  }
  return false;
};

export const parseEmails = (str) =>
  (str || "")
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean);

export const byteToMB = (bytes) => {
  return bytes / (1024 * 1024);
};

export const byteToKB = (bytes) => {
  return bytes / 1024;
};
